<template>
  <div class="table-content qaqc-consistency-check-wrap qaqc-equilibrium-plot-wrap">
    <div class="table-wrap">
      <div class="table-title-div mt-4">
        <p class="table-title">Equilibrium Consistency Analysis</p>
        <div class="qaqc-scorecard-header-actions">
          <label v-if="expandableRowCount" class="qaqc-expand-all-wrap">
            <input
              ref="expandAllCheckboxInput"
              type="checkbox"
              class="qaqc-expand-all-checkbox"
              aria-label="Expand all rows with non-compliant samples"
              @change="onExpandAllCheckboxChange"
            />
            <span class="qaqc-expand-all-text">Expand all</span>
          </label>
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="medium"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-consistency-check-body">
        <div class="qaqc-consistency-subsection">
          <p class="qaqc-consistency-desc qaqc-equilibrium-desc">
            Pass rates for Modified Wilson, Buckley, and Hoffman plot linearity across all samples. Click on non-compliant count to see affected samples.
          </p>
          <div v-if="rows.length" class="qaqc-consistency-table-wrap">
            <table class="qaqc-consistency-table">
              <thead>
                <tr class="qaqc-consistency-thead">
                  <th class="qaqc-consistency-th-property">Equilibrium Plot</th>
                  <th class="qaqc-consistency-th-num">Compliant</th>
                  <th class="qaqc-consistency-th-num">Non-Compliant</th>
                  <th class="qaqc-consistency-th-num">Compliance Rate</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(item, idx) in rowsWithChips" :key="'eq-' + idx">
                  <tr class="qaqc-consistency-tr">
                    <td class="qaqc-consistency-td-property">
                      <div class="qaqc-consistency-property-label">{{ item.row.displayName }}</div>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.compliant }}</td>
                    <td
                      class="qaqc-consistency-td-num qaqc-consistency-nc-cell"
                      :class="{ 'qaqc-consistency-nc-cell--interactive': rowHasNonCompliant(item.row) }"
                      role="button"
                      :tabindex="rowHasNonCompliant(item.row) ? 0 : -1"
                      @click="rowHasNonCompliant(item.row) ? toggleExpand(idx) : null"
                      @keydown.enter.prevent="rowHasNonCompliant(item.row) ? toggleExpand(idx) : null"
                      @keydown.space.prevent="rowHasNonCompliant(item.row) ? toggleExpand(idx) : null"
                    >
                      <span class="qaqc-consistency-nc-inner">
                        <v-icon
                          v-if="rowHasNonCompliant(item.row)"
                          size="small"
                          class="qaqc-consistency-nc-chevron"
                        >{{ rowDetailExpanded(idx) ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
                        <span class="qaqc-consistency-nc-value">{{ item.row.nonCompliant }}</span>
                      </span>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.complianceRate }}</td>
                  </tr>
                  <tr
                    v-if="rowDetailExpanded(idx) && rowHasNonCompliant(item.row)"
                    class="qaqc-consistency-expand-row"
                  >
                    <td colspan="4" class="qaqc-consistency-expand-cell">
                      <div class="qaqc-consistency-expand-inner">
                        <span class="qaqc-consistency-sublabel">Non-compliant samples (click to view QC details)</span>
                        <div v-if="item.chips.length" class="qaqc-consistency-chips">
                          <span
                            v-for="(chip, cidx) in item.chips"
                            :key="chipDomKey(idx, chip, cidx)"
                            class="qaqc-consistency-chip qaqc-consistency-check-chip"
                            :class="chipClasses(idx, chip, cidx)"
                            role="button"
                            :tabindex="chipTabindex(idx, chip, cidx)"
                            :aria-busy="chipIsLoading(idx, chip, cidx)"
                            @click.stop="openSampleQc(chip, idx, cidx)"
                            @keydown.enter.prevent="activateChip(chip, idx, cidx)"
                            @keydown.space.prevent="activateChip(chip, idx, cidx)"
                          >
                            <v-progress-circular
                              v-if="chipIsLoading(idx, chip, cidx)"
                              indeterminate
                              size="18"
                              width="2"
                              color="primary"
                              class="qaqc-consistency-check-chip-loader"
                            />
                            <template v-else>{{ chipLabel(chip) }}</template>
                          </span>
                        </div>
                        <div v-else class="qaqc-consistency-no-ids">No sample IDs returned for this check.</div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-else class="qaqc-equilibrium-empty">No equilibrium plot linearity data available.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { csvEscapeCell } from "./distributionCsvHelpers.js";
import {
  matrixContextToApiFilters,
  routerQueryFromApiFilters,
  pickSamplesListQueryForRouter,
} from "./qaqcSamplesListQuery.js";
import {
  resolveQaqcFailedSampleNavigation,
  pickTestFromSamplePayload,
  pushQaqcOverviewFailedSampleRoute,
} from "./qaqcFailedSampleNavigation.js";

export default {
  name: "EquilibriumPlotLinearitySection",
  props: {
    rows: { type: Array, default: () => [] },
    matrixTestContext: {
      type: String,
      default: "dl",
      validator: (v) => ["dl", "separator"].includes(v),
    },
  },
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      isCollapsed: false,
      expandedIndices: [],
      loadingChipKey: null,
    };
  },
  computed: {
    navTestKind() {
      return this.matrixTestContext === "separator" ? "separator" : "dl";
    },
    rowsWithChips() {
      return (this.rows || []).map((row) => ({
        row,
        chips: this.buildChips(row),
      }));
    },
    expandableRowCount() {
      let n = 0;
      this.rowsWithChips.forEach((item) => {
        if (this.rowHasNonCompliant(item.row)) n += 1;
      });
      return n;
    },
    allExpandableExpanded() {
      const expandable = [];
      this.rowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) expandable.push(idx);
      });
      if (expandable.length === 0) return false;
      return expandable.every((i) => this.expandedIndices.includes(i));
    },
    expandAllIndeterminate() {
      const expandable = [];
      this.rowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) expandable.push(idx);
      });
      if (expandable.length < 2) return false;
      let open = 0;
      expandable.forEach((i) => {
        if (this.expandedIndices.includes(i)) open += 1;
      });
      return open > 0 && open < expandable.length;
    },
  },
  watch: {
    rows() {
      this.expandedIndices = [];
      this.loadingChipKey = null;
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    expandableRowCount() {
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    expandedIndices: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncExpandAllCheckboxDom());
      },
    },
  },
  mounted() {
    this.$nextTick(() => this.syncExpandAllCheckboxDom());
  },
  methods: {
    rowDetailExpanded(idx) {
      return this.expandedIndices.includes(idx);
    },
    nonCompliantCountNum(row) {
      const raw = row?.nonCompliant;
      if (raw === undefined || raw === null || raw === "-") return 0;
      const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? 0 : n;
    },
    rowHasNonCompliant(row) {
      return this.nonCompliantCountNum(row) > 0;
    },
    toggleExpand(idx) {
      const item = this.rowsWithChips[idx];
      if (!item || !this.rowHasNonCompliant(item.row)) return;
      const pos = this.expandedIndices.indexOf(idx);
      if (pos === -1) this.expandedIndices.push(idx);
      else this.expandedIndices.splice(pos, 1);
    },
    syncExpandAllCheckboxDom() {
      const el = this.$refs.expandAllCheckboxInput;
      if (!el) return;
      el.indeterminate = this.expandAllIndeterminate;
      el.checked = this.allExpandableExpanded;
    },
    onExpandAllCheckboxChange(e) {
      const expandable = [];
      this.rowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) expandable.push(idx);
      });
      if (expandable.length === 0) return;
      if (e.target.checked) {
        this.expandedIndices = [...expandable];
      } else {
        this.expandedIndices = [];
      }
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    buildChips(row) {
      const fs = row?.failedSamples;
      if (Array.isArray(fs) && fs.length) return fs;
      return (row?.fluidSampleIds || []).map((fid) => ({ fluidSampleID: String(fid), id: "" }));
    },
    chipInstanceKey(rowIdx, chip, chipIdx) {
      return `eq:r${rowIdx}:i${chipIdx}:${String(chip.id ?? "")}:f${String(chip.fluidSampleID ?? "")}`;
    },
    chipDomKey(rowIdx, chip, chipIdx) {
      return this.chipInstanceKey(rowIdx, chip, chipIdx);
    },
    chipIsLoading(rowIdx, chip, chipIdx) {
      return this.loadingChipKey === this.chipInstanceKey(rowIdx, chip, chipIdx);
    },
    chipIsBusyOther(rowIdx, chip, chipIdx) {
      return this.loadingChipKey != null && !this.chipIsLoading(rowIdx, chip, chipIdx);
    },
    chipClasses(rowIdx, chip, chipIdx) {
      return {
        "qaqc-consistency-check-chip--loading": this.chipIsLoading(rowIdx, chip, chipIdx),
        "qaqc-consistency-check-chip--disabled": this.chipIsBusyOther(rowIdx, chip, chipIdx),
      };
    },
    chipTabindex(rowIdx, chip, chipIdx) {
      if (this.chipIsLoading(rowIdx, chip, chipIdx)) return -1;
      if (this.chipIsBusyOther(rowIdx, chip, chipIdx)) return -1;
      return 0;
    },
    chipLabel(chip) {
      return chip.fluidSampleID || chip.id || "";
    },
    resolveTenantId() {
      return getTenantPrefix() || "";
    },
    pushFailedSampleTestRoute(sampleId, routeTestType, testId, wellName, navigationTarget) {
      const listQuery = {
        ...routerQueryFromApiFilters(matrixContextToApiFilters(this.navTestKind)),
        ...pickSamplesListQueryForRouter(this.$route.query || {}),
      };
      pushQaqcOverviewFailedSampleRoute(this.$router, {
        tenantId: this.resolveTenantId(),
        mongoSampleId: sampleId,
        testId,
        routeTestType,
        well: wellName,
        navigationTarget,
        listQuery,
      });
    },
    samplePayloadFromViewSummary(res) {
      const d = res?.data?.data;
      if (!d) return null;
      if (d.sample && typeof d.sample === "object") return d.sample;
      const list = d.samples;
      if (Array.isArray(list) && list[0]) return list[0];
      return null;
    },
    async fetchSamplePayloadForChip(chip) {
      if (chip.id) {
        const res = await api.get(`public/api/samples/${chip.id}?viewSummary=true`);
        return this.samplePayloadFromViewSummary(res);
      }
      const fid = chip.fluidSampleID;
      if (!fid) return null;
      const res = await api.get("public/api/samples", {
        params: {
          fluidSampleID: `["${String(fid)}"]`,
          viewSummary: true,
          pageNumber: 1,
          pageSize: 10,
        },
      });
      const samples = res?.data?.data?.samples;
      return Array.isArray(samples) && samples[0] ? samples[0] : null;
    },
    activateChip(chip, rowIdx, chipIdx) {
      if (this.loadingChipKey) return;
      this.openSampleQc(chip, rowIdx, chipIdx);
    },
    async openSampleQc(chip, rowIdx, chipIdx) {
      const key = this.chipInstanceKey(rowIdx, chip, chipIdx);
      if (!key || this.loadingChipKey) return;
      this.loadingChipKey = key;
      try {
        const s = await this.fetchSamplePayloadForChip(chip);
        if (!s) return;
        const mongoId = s.ID || s._id || s.id;
        if (!mongoId) return;

        const { testId, routeTestType } = pickTestFromSamplePayload(s, this.navTestKind);
        const row = this.rows[rowIdx];
        const navigationTarget = resolveQaqcFailedSampleNavigation({
          criterionName: row?.displayName || "",
          source: "dlEquilibrium",
          testKind: this.navTestKind,
        });

        if (testId && routeTestType) {
          const well = s.location?.well ?? s.wellName ?? s.well;
          this.pushFailedSampleTestRoute(mongoId, routeTestType, testId, well, navigationTarget);
        } else {
          const well = s.location?.well ?? s.wellName ?? s.well;
          const q = {
            type: "wells",
            ...routerQueryFromApiFilters(matrixContextToApiFilters(this.navTestKind)),
            ...pickSamplesListQueryForRouter(this.$route.query || {}),
          };
          if (well) {
            this.$router.push({ name: "QAQCDetailView", params: { well: String(well) }, query: q });
          } else {
            this.$router.push({ name: "QAQCDetailView", query: q });
          }
        }
      } catch {
        void 0;
      } finally {
        this.loadingChipKey = null;
      }
    },
    downloadTableCSV() {
      if (!this.rows.length) return;
      const fileName = "equilibrium_plot_linearity_analysis.csv";
      const csvHeader = ["Equilibrium Plot", "Compliant", "Non-Compliant", "Compliance Rate", "Sample IDs"];
      const csvRows = this.rows.map((row) =>
        [
          csvEscapeCell(row.displayName ?? ""),
          csvEscapeCell(row.compliant ?? ""),
          csvEscapeCell(row.nonCompliant ?? ""),
          csvEscapeCell(row.complianceRate ?? ""),
          csvEscapeCell((row.fluidSampleIds || []).join(", ")),
        ].join(",")
      );
      const csvContent =
        "\ufeff" + [csvHeader.map(csvEscapeCell).join(","), ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>
